/**
 * @file src/lib/sampleProfiles.ts
 * @description Pre-built realistic sample profiles for top empirical user roles.
 * Used during intake when a user continues without a resume, allowing them to
 * immediately experience Hirecarta features (job fit, resume tailoring, coach)
 * before replacing with their real resume.
 */

import { UserBackground } from "@/types";

/**
 * System prompt specification for generating sample profile data.
 * Mandates that all bullet points in experience descriptions begin with a hyphen ("- ").
 */
export const SAMPLE_PROFILE_SYSTEM_PROMPT = `You are an expert career profiler. Generate a realistic, authentic professional background for the requested role.
CRITICAL FORMATTING RULES:
1. Every bullet in work experience descriptions MUST begin with a hyphen ("- ") followed by a space.
2. Separate individual bullet points with a newline character.
3. Quantify achievements with concrete metrics, percentages, and business outcomes.
4. Keep all output professional, concise, and scannable.`;

/**
 * Helper to ensure every bullet line in an experience description begins with a hyphen ("- ").
 */
export function formatBulletDescription(text: string): string {
  return text
    .split("\n")
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("-") || trimmed.startsWith("•") || trimmed.startsWith("*")) {
        return `- ${trimmed.replace(/^[-•*]\s*/, "")}`;
      }
      return `- ${trimmed}`;
    })
    .filter(Boolean)
    .join("\n");
}

export const SAMPLE_ROLE_OPTIONS = [
  "Software Engineer",
  "Product Manager",
  "Data Analyst",
  "Project Manager",
  "Customer Success Manager",
  "Sales Rep",
  "Marketing Manager",
  "Administrative Assistant",
  "Operations Manager",
] as const;

export type SampleRoleOption = (typeof SAMPLE_ROLE_OPTIONS)[number];

interface UserIdentity {
  name?: string | null;
  email?: string | null;
}

export function getSampleProfile(role: string, user?: UserIdentity): UserBackground {
  const candidateName = user?.name?.trim() || "Alex Morgan";
  const candidateEmail = user?.email?.trim() || "alex.morgan@example.com";

  let profile: UserBackground;

  switch (role) {
    case "Software Engineer":
      profile = {
        isSampleProfile: true,
        basicInfo: {
          name: candidateName,
          email: candidateEmail,
          phoneNumber: "(555) 234-5678",
          location: "San Francisco, CA",
          linkedinUrl: "https://linkedin.com/in/alexmorgan-sample",
          websiteUrl: "https://github.com/alexmorgan-sample",
        },
        summary: {
          expertise:
            "Full-stack software engineer with 5 years of experience building distributed web services and scalable APIs using TypeScript, React, and Node.js.",
          passions:
            "Passionate about developer productivity, clean software architecture, and real-time interactive user interfaces.",
          goals:
            "Seeking a senior engineering role where I can lead architectural initiatives and mentor junior team members.",
        },
        workExperiences: [
          {
            company: "Apex Cloud Systems",
            role: "Software Engineer",
            startDate: "2023-01",
            endDate: "Present",
            companyInfo: "B2B SaaS data infrastructure provider",
            description:
              "- Architected and deployed scalable REST and GraphQL microservices supporting 250k daily active users.\n- Optimized PostgreSQL database queries, reducing p95 latency from 420ms to 85ms across core transaction endpoints.\n- Led migration of client-facing dashboard to Next.js and Tailwind CSS, improving Lighthouse performance score from 64 to 96.",
          },
          {
            company: "Beacon Media Labs",
            role: "Software Engineer",
            startDate: "2021-06",
            endDate: "2022-12",
            companyInfo: "Digital publishing and content delivery platform",
            description:
              "- Built real-time websocket notification pipeline that processed over 1.2M daily events with 99.98% uptime.\n- Collaborated with product designers to implement responsive, accessible frontend component library in React.\n- Introduced automated CI/CD GitHub Actions workflows, cutting team deployment time by 45%.",
          },
        ],
        additionalExperiences: [],
        education: [
          {
            institution: "University of California, Berkeley",
            degree: "B.S. in Computer Science",
            date: "2021",
            accomplishments: "Dean's Honor List, President of Open Source Developers Club",
          },
        ],
        customSections: [
          {
            sectionName: "Skills",
            bullets: [
              "Languages: TypeScript, JavaScript, Python, Go, SQL",
              "Frontend & Frameworks: React, Next.js, Node.js, Tailwind CSS, Redux, TanStack Query",
              "Backend & Cloud: PostgreSQL, Redis, Docker, AWS (S3, ECS, Lambda), REST, GraphQL",
              "Practices: CI/CD, Test-Driven Development (Jest, Vitest), Microservices, System Architecture",
            ],
          },
        ],
      };
      break;

    case "Product Manager":
      profile = {
        isSampleProfile: true,
        basicInfo: {
          name: candidateName,
          email: candidateEmail,
          phoneNumber: "(555) 345-6789",
          location: "New York, NY",
          linkedinUrl: "https://linkedin.com/in/alexmorgan-sample",
          websiteUrl: "",
        },
        summary: {
          expertise:
            "Customer-centric product manager with 4 years of experience driving product roadmaps from discovery through launch in fast-paced SaaS organizations.",
          passions:
            "Passionate about continuous customer discovery, data-backed hypothesis testing, and intuitive user experiences.",
          goals:
            "Looking to drive growth and product adoption for high-impact consumer or enterprise software products.",
        },
        workExperiences: [
          {
            company: "Pulse Analytics",
            role: "Product Manager",
            startDate: "2023-03",
            endDate: "Present",
            companyInfo: "Workplace engagement and feedback software",
            description:
              "- Spearheaded redesign of self-serve onboarding flow, increasing trial-to-paid conversion by 28% in 6 months.\n- Conducted 60+ customer discovery interviews to synthesize user journey pain points into prioritized product roadmaps.\n- Partnered with engineering and design leads in two-week agile sprints, achieving 94% on-time release rate.",
          },
          {
            company: "Novus Commerce",
            role: "Associate Product Manager",
            startDate: "2021-08",
            endDate: "2023-02",
            companyInfo: "E-commerce checkout optimization platform",
            description:
              "- Ran 18 multivariate A/B tests on cart checkout, yielding $1.4M in incremental annualized merchant revenue.\n- Authored detailed PRDs, user stories, and acceptance criteria for mobile responsive checkout experiences.\n- Tracked core KPIs in Mixpanel and SQL to evaluate feature adoption and retention curves.",
          },
        ],
        additionalExperiences: [],
        education: [
          {
            institution: "New York University",
            degree: "B.S. in Information Systems",
            date: "2021",
            accomplishments: "Magna Cum Laude, Capstone Product Excellence Award",
          },
        ],
        customSections: [
          {
            sectionName: "Skills",
            bullets: [
              "Product Strategy: Roadmapping, OKR Definition, Opportunity Solution Trees, Agile/Scrum",
              "Analytics: SQL, PostHog, Mixpanel, Google Analytics, Amplitude, Tableau",
              "User Research: User Interviews, Usability Testing, Persona Mapping, Surveys",
              "Tools: Jira, Linear, Figma, Notion, Miro, Confluence",
            ],
          },
        ],
      };
      break;

    case "Data Analyst":
      profile = {
        isSampleProfile: true,
        basicInfo: {
          name: candidateName,
          email: candidateEmail,
          phoneNumber: "(555) 456-7890",
          location: "Chicago, IL",
          linkedinUrl: "https://linkedin.com/in/alexmorgan-sample",
          websiteUrl: "",
        },
        summary: {
          expertise:
            "Quantitative data analyst with 4 years of experience transforming raw transactional and behavioral data into actionable business recommendations.",
          passions:
            "Passionate about storytelling with data, automated business intelligence reporting, and metric reliability.",
          goals:
            "Seeking an analytics role focused on customer behavior, lifecycle modeling, and executive decision support.",
        },
        workExperiences: [
          {
            company: "Crestview Health Solutions",
            role: "Data Analyst",
            startDate: "2023-02",
            endDate: "Present",
            companyInfo: "Healthcare operations and logistics analytics",
            description:
              "- Built executive Tableau and Looker dashboards monitoring patient throughput and staffing efficiency for 14 regional centers.\n- Wrote production SQL queries over Snowflake warehouse to identify patient scheduling bottlenecks, saving $320k annually.\n- Automated weekly executive reporting via Python scripts, saving 8 hours of manual data extraction each week.",
          },
          {
            company: "Vanguard Retail Group",
            role: "Junior Data Analyst",
            startDate: "2021-07",
            endDate: "2023-01",
            companyInfo: "Omnichannel retail chain",
            description:
              "- Modeled customer churn indicators using logistic regression in Python, identifying top early risk factors.\n- Maintained data cleanliness and validation rules across 3M+ customer records in PostgreSQL.\n- Delivered bi-weekly cohort retention analysis to marketing and inventory management teams.",
          },
        ],
        additionalExperiences: [],
        education: [
          {
            institution: "University of Illinois Urbana-Champaign",
            degree: "B.S. in Statistics and Applied Data Science",
            date: "2021",
            accomplishments: "Dean's List, Data Analytics Association Vice President",
          },
        ],
        customSections: [
          {
            sectionName: "Skills",
            bullets: [
              "Querying & Databases: SQL, PostgreSQL, Snowflake, BigQuery, MySQL",
              "Data Visualization: Tableau, Looker Studio, Power BI, Metabase",
              "Programming: Python (Pandas, NumPy, Matplotlib), R, Git",
              "Methodologies: Cohort Analysis, A/B Testing, Regression Modeling, KPI Tracking",
            ],
          },
        ],
      };
      break;

    case "Project Manager":
      profile = {
        isSampleProfile: true,
        basicInfo: {
          name: candidateName,
          email: candidateEmail,
          phoneNumber: "(555) 567-8901",
          location: "Dallas, TX",
          linkedinUrl: "https://linkedin.com/in/alexmorgan-sample",
          websiteUrl: "",
        },
        summary: {
          expertise:
            "PMP-certified project manager with 5 years of experience delivering cross-functional technology and operational initiatives on schedule and within budget.",
          passions:
            "Passionate about eliminating team blockers, operational excellence, and clear stakeholder communication.",
          goals:
            "Looking to lead enterprise-wide transformation and software delivery programs.",
        },
        workExperiences: [
          {
            company: "Horizon Tech Logistics",
            role: "Project Manager",
            startDate: "2022-10",
            endDate: "Present",
            companyInfo: "Supply chain and warehouse technology solutions",
            description:
              "- Managed delivery of $2.2M automated warehouse tracking upgrade across 4 distribution hubs on time and 6% under budget.\n- Coordinated cross-functional teams of 22 software engineers, hardware technicians, and field managers.\n- Standardized sprint velocity reporting and risk registers, cutting delivery variance by 30%.",
          },
          {
            company: "Summit Digital Services",
            role: "Associate Project Manager",
            startDate: "2020-08",
            endDate: "2022-09",
            companyInfo: "Technology consulting and managed systems",
            description:
              "- Oversaw client onboarding implementations for 18 mid-market enterprise software rollouts.\n- Facilitated sprint planning, daily stand-ups, backlog grooming, and retrospective meetings in Jira.\n- Prepared weekly steering committee status dashboards for executive leadership.",
          },
        ],
        additionalExperiences: [],
        education: [
          {
            institution: "Texas A&M University",
            degree: "B.B.A. in Supply Chain & Information Operations",
            date: "2020",
            accomplishments: "PMP Certified (2023), Certified ScrumMaster (CSM)",
          },
        ],
        customSections: [
          {
            sectionName: "Skills",
            bullets: [
              "Project Governance: Agile, Scrum, Kanban, Waterfall, PMP Framework, Risk Management",
              "Software: Jira, Asana, Monday.com, Confluence, Microsoft Project, Smartsheet",
              "Leadership: Stakeholder Alignment, Vendor Negotiation, Resource Allocation, Budgeting",
              "Reporting: RAID Logs, Milestone Tracking, Executive Status Briefings, KPI Dashboards",
            ],
          },
        ],
      };
      break;

    case "Customer Success Manager":
      profile = {
        isSampleProfile: true,
        basicInfo: {
          name: candidateName,
          email: candidateEmail,
          phoneNumber: "(555) 678-9012",
          location: "Austin, TX",
          linkedinUrl: "https://linkedin.com/in/alexmorgan-sample",
          websiteUrl: "",
        },
        summary: {
          expertise:
            "High-touch customer success manager with 4 years of experience driving product adoption, net revenue retention, and renewals for B2B SaaS accounts.",
          passions:
            "Passionate about consultative onboarding, voice-of-customer advocacy, and turning users into brand champions.",
          goals:
            "Seeking a customer success role managing a portfolio of mid-market or enterprise accounts.",
        },
        workExperiences: [
          {
            company: "Catalyst Cloud Solutions",
            role: "Customer Success Manager",
            startDate: "2023-01",
            endDate: "Present",
            companyInfo: "Collaborative workflow software for enterprise teams",
            description:
              "- Managed a $3.8M ARR portfolio of 45 enterprise accounts, achieving 114% Net Revenue Retention (NRR).\n- Conducted monthly business reviews (QBRs) and executive check-ins to map client goals to product capabilities.\n- Reduced first-year account churn from 12% to 4.5% through a structured 90-day onboarding program.",
          },
          {
            company: "Echo Interactive",
            role: "Customer Onboarding Specialist",
            startDate: "2021-05",
            endDate: "2022-12",
            companyInfo: "Customer experience and engagement platform",
            description:
              "- Onboarded 120+ commercial client teams, reducing time-to-first-value (TTFV) from 35 days to 14 days.\n- Created customer enablement webinars, video tutorials, and interactive knowledge base documentation.\n- Identified expansion opportunities resulting in $240k in annual contract value upsells.",
          },
        ],
        additionalExperiences: [],
        education: [
          {
            institution: "University of Texas at Austin",
            degree: "B.A. in Communications and Business Foundations",
            date: "2021",
            accomplishments: "Customer Success Leadership Certification (Gainsight)",
          },
        ],
        customSections: [
          {
            sectionName: "Skills",
            bullets: [
              "Customer Success: Net Revenue Retention (NRR), Churn Prevention, QBRs, Customer Journey Mapping",
              "CRM & CS Platforms: Salesforce, HubSpot, Gainsight, ChurnZero, Zendesk",
              "Communication: Executive Stakeholder Presentation, Conflict Resolution, Product Training",
              "Analytics: Account Health Scoring, NPS/CSAT Tracking, Usage Analytics",
            ],
          },
        ],
      };
      break;

    case "Sales Rep":
      profile = {
        isSampleProfile: true,
        basicInfo: {
          name: candidateName,
          email: candidateEmail,
          phoneNumber: "(555) 789-0123",
          location: "Atlanta, GA",
          linkedinUrl: "https://linkedin.com/in/alexmorgan-sample",
          websiteUrl: "",
        },
        summary: {
          expertise:
            "Results-driven B2B sales professional with 4 years of quota-crushing performance across outbound pipeline development and full-cycle deal execution.",
          passions:
            "Passionate about consultative discovery, value-based selling, and accelerating revenue growth.",
          goals:
            "Seeking an Account Executive or Senior Sales role within an ambitious software or tech organization.",
        },
        workExperiences: [
          {
            company: "Sterling Technology Partners",
            role: "Sales Representative / Account Executive",
            startDate: "2023-04",
            endDate: "Present",
            companyInfo: "Enterprise cybersecurity and compliance software",
            description:
              "- Achieved 128% of annual sales quota in 2024, closing $1.1M in new ARR across 34 commercial accounts.\n- Generated 40% of pipeline through personalized multichannel outbound campaigns (cold email, phone, LinkedIn).\n- Led end-to-end sales cycle including discovery, custom product demonstrations, contract negotiation, and closing.",
          },
          {
            company: "Vanguard Growth Media",
            role: "Sales Development Representative (SDR)",
            startDate: "2021-09",
            endDate: "2023-03",
            companyInfo: "Performance marketing and demand generation software",
            description:
              "- Consistently exceeded monthly outbound qualification targets, booking 22+ qualified demos per month.\n- Ranked #1 SDR out of 16 reps in Q3 and Q4 2022 for opportunity-to-close conversion rate.\n- Partnered closely with Account Executives to tailor pitch decks to executive decision-makers.",
          },
        ],
        additionalExperiences: [],
        education: [
          {
            institution: "University of Georgia",
            degree: "B.B.A. in Marketing & Professional Sales",
            date: "2021",
            accomplishments: "Collegiate Sales Competition Finalist, Magna Cum Laude",
          },
        ],
        customSections: [
          {
            sectionName: "Skills",
            bullets: [
              "Sales Methodology: MEDDIC, Command of the Message, Challenger Sale, Solution Selling",
              "Pipeline Generation: Cold Calling, Multichannel Prospecting, Objection Handling, Lead Qualification",
              "Sales Tech Stack: Salesforce, HubSpot CRM, Outreach.io, Apollo.io, Gong, ZoomInfo",
              "Closing: Value Proposition Delivery, Contract Negotiation, Executive Discovery",
            ],
          },
        ],
      };
      break;

    case "Marketing Manager":
      profile = {
        isSampleProfile: true,
        basicInfo: {
          name: candidateName,
          email: candidateEmail,
          phoneNumber: "(555) 890-1234",
          location: "Boston, MA",
          linkedinUrl: "https://linkedin.com/in/alexmorgan-sample",
          websiteUrl: "",
        },
        summary: {
          expertise:
            "Full-funnel marketing manager with 5 years of experience scaling organic and paid customer acquisition channels for digital products.",
          passions:
            "Passionate about compelling brand narrative, ROI-positive demand generation, and lifecycle campaign optimization.",
          goals:
            "Looking to spearhead growth marketing and brand strategy for an innovative product.",
        },
        workExperiences: [
          {
            company: "Beacon Health & Wellness",
            role: "Marketing Manager",
            startDate: "2023-02",
            endDate: "Present",
            companyInfo: "Direct-to-consumer health subscription platform",
            description:
              "- Managed $650k annual growth marketing budget across Google Ads, Meta, and content partnerships, achieving 3.4x ROAS.\n- Launched automated email lifecycle onboarding flows, lifting 30-day subscriber retention by 22%.\n- Overhauled brand content and SEO strategy, increasing organic search traffic from 35k to 120k monthly visits.",
          },
          {
            company: "Stratton Digital Agency",
            role: "Marketing Specialist",
            startDate: "2020-07",
            endDate: "2023-01",
            companyInfo: "Growth marketing and content agency",
            description:
              "- Designed and executed integrated digital marketing campaigns for 12 technology and consumer clients.\n- Coordinated product launches, press releases, and co-marketing partnerships with industry creators.\n- Monitored campaign attribution, CAC, and LTV using Google Analytics and HubSpot.",
          },
        ],
        additionalExperiences: [],
        education: [
          {
            institution: "Boston University",
            degree: "B.S. in Communications and Digital Marketing",
            date: "2020",
            accomplishments: "Dean's List, HubSpot Inbound Marketing Certified",
          },
        ],
        customSections: [
          {
            sectionName: "Skills",
            bullets: [
              "Growth & Acquisition: Search Engine Optimization (SEO), SEM, Google Ads, Meta Ads, Paid Social",
              "Content & Brand: Copywriting, Product Messaging, Brand Strategy, Email Marketing",
              "Marketing Technology: HubSpot, Mailchimp, Google Analytics 4, WordPress, Semrush, Canva",
              "Analytics: Multi-touch Attribution, ROAS, CAC/LTV Analysis, A/B Split Testing",
            ],
          },
        ],
      };
      break;

    case "Administrative Assistant":
      profile = {
        isSampleProfile: true,
        basicInfo: {
          name: candidateName,
          email: candidateEmail,
          phoneNumber: "(555) 901-2345",
          location: "Philadelphia, PA",
          linkedinUrl: "https://linkedin.com/in/alexmorgan-sample",
          websiteUrl: "",
        },
        summary: {
          expertise:
            "Proactive administrative professional with 4 years of experience coordinating office operations, executive scheduling, and project logistics in corporate settings.",
          passions:
            "Passionate about detail-oriented organization, seamless calendar coordination, and exceptional internal client service.",
          goals:
            "Seeking an Administrative or Executive Assistant role supporting department heads or C-level leadership.",
        },
        workExperiences: [
          {
            company: "Keystone Financial Advisory",
            role: "Administrative Assistant",
            startDate: "2022-09",
            endDate: "Present",
            companyInfo: "Wealth management and corporate advisory firm",
            description:
              "- Coordinated calendar management and international travel itineraries for 3 managing directors with zero booking conflicts.\n- Managed client communications and prepared polished briefing dossiers ahead of high-stakes stakeholder meetings.\n- Streamlined office vendor management and billing workflows, reducing monthly administrative overhead by 15%.",
          },
          {
            company: "Pennside Legal Group",
            role: "Office Assistant",
            startDate: "2020-11",
            endDate: "2022-08",
            companyInfo: "Commercial law and civil practice",
            description:
              "- Organized electronic and physical filing systems for 400+ confidential client records in compliance with standards.\n- Greeted visiting corporate clients and managed incoming phone inquiries across 12 extensions.\n- Assisted litigation paralegals with document drafting, proofreading, and meeting schedule preparations.",
          },
        ],
        additionalExperiences: [],
        education: [
          {
            institution: "Temple University",
            degree: "B.A. in Business Administration",
            date: "2020",
            accomplishments: "Dean's List, Certified Administrative Professional (CAP)",
          },
        ],
        customSections: [
          {
            sectionName: "Skills",
            bullets: [
              "Administration: Executive Scheduling, Travel Logistics, Expense Reporting, Vendor Management",
              "Office Tools: Microsoft 365 (Word, Excel, PowerPoint, Outlook), Google Workspace, Zoom, Slack",
              "Operations: Event Coordination, Meeting Minutes, Records Management, Invoicing",
              "Soft Skills: Confidentiality, Proactive Problem Solving, Written Communication, Multitasking",
            ],
          },
        ],
      };
      break;

    case "Operations Manager":
      profile = {
        isSampleProfile: true,
        basicInfo: {
          name: candidateName,
          email: candidateEmail,
          phoneNumber: "(555) 012-3456",
          location: "Denver, CO",
          linkedinUrl: "https://linkedin.com/in/alexmorgan-sample",
          websiteUrl: "",
        },
        summary: {
          expertise:
            "Operations manager with 5 years of experience optimizing operational workflows, supply chain systems, and team productivity in scaling businesses.",
          passions:
            "Passionate about lean process improvement, workflow automation, and building resilient operational foundations.",
          goals:
            "Seeking an operations management position to scale day-to-day business processes and boost operational margins.",
        },
        workExperiences: [
          {
            company: "Alpine Logistics Network",
            role: "Operations Manager",
            startDate: "2022-11",
            endDate: "Present",
            companyInfo: "Regional freight and fulfillment logistics provider",
            description:
              "- Directed daily dispatch and facility operations across a 45,000 sq ft distribution facility with 35 staff.\n- Implemented automated inventory reconciliation SOPs, cutting shipment error rate from 4.1% to under 0.8%.\n- Renegotiated packaging and carrier partner contracts, generating $185k in annual operating cost savings.",
          },
          {
            company: "Peak Direct Consumer Goods",
            role: "Operations Coordinator",
            startDate: "2020-06",
            endDate: "2022-10",
            companyInfo: "E-commerce outdoor equipment brand",
            description:
              "- Oversaw vendor compliance, order fulfillment SLA tracking, and returns logistics.\n- Developed cross-departmental dashboards tracking on-time delivery rates and customer inquiry resolution times.\n- Assisted in onboarding and training 20+ warehouse fulfillment associates.",
          },
        ],
        additionalExperiences: [],
        education: [
          {
            institution: "University of Colorado Boulder",
            degree: "B.S. in Business Administration (Operations Management)",
            date: "2020",
            accomplishments: "Six Sigma Green Belt Certification (2022)",
          },
        ],
        customSections: [
          {
            sectionName: "Skills",
            bullets: [
              "Operations: Process Improvement, Lean Six Sigma, SOP Development, Supply Chain Management",
              "Tools: ERP Systems (NetSuite, SAP), Excel / Google Sheets (Advanced Formulas), Asana, Notion",
              "Financial: Budget Oversight, Vendor Procurement, Operating Cost Reduction, Margin Analysis",
              "People Leadership: Team Supervision, Safety Compliance, Workflow Training, SLA Tracking",
            ],
          },
        ],
      };
      break;

    default:
      // Generic fallback for custom "Other" roles
      profile = {
        isSampleProfile: true,
        basicInfo: {
          name: candidateName,
          email: candidateEmail,
          phoneNumber: "(555) 123-4567",
          location: "San Francisco, CA",
          linkedinUrl: "https://linkedin.com/in/candidate-sample",
          websiteUrl: "",
        },
        summary: {
          expertise: `Experienced professional with a proven track record of delivering measurable impact as a ${role}.`,
          passions: "Passionate about solving complex problems, operational efficiency, and cross-functional teamwork.",
          goals: `Seeking challenging opportunities to contribute expertise and lead high-value initiatives as a ${role}.`,
        },
        workExperiences: [
          {
            company: "Apex Solutions Group",
            role: role,
            startDate: "2023-01",
            endDate: "Present",
            companyInfo: "Industry-leading enterprise solutions provider",
            description: `- Spearheaded key operational and strategic initiatives as ${role}, delivering 24% year-over-year performance improvements.\n- Partnered with cross-functional leadership to standardize workflows and align project milestones with organizational objectives.\n- Mentored and guided team members to elevate overall team output and execution velocity.`,
          },
          {
            company: "Summit Innovations",
            role: `Associate ${role}`,
            startDate: "2021-06",
            endDate: "2022-12",
            companyInfo: "Fast-growing technology and consulting organization",
            description: `- Executed day-to-day responsibilities with high attention to detail and customer satisfaction.\n- Collaborated on multi-phase project rollouts that improved operational turnaround time by 30%.\n- Analyzed core performance metrics to identify and eliminate workflow bottlenecks.`,
          },
        ],
        additionalExperiences: [],
        education: [
          {
            institution: "State University",
            degree: "Bachelor of Science",
            date: "2021",
            accomplishments: "Dean's Honor Roll, Academic Excellence Award",
          },
        ],
        customSections: [
          {
            sectionName: "Skills",
            bullets: [
              `Core Competencies: ${role} Strategy, Project Execution, Workflow Optimization`,
              "Tools & Software: Modern Productivity Suites, Cloud Collaboration Platforms, Data Analysis",
              "Leadership: Cross-Functional Collaboration, Stakeholder Communication, Problem Solving",
            ],
          },
        ],
      };
      break;
  }

  // Enforce hyphen formatting on every experience bullet across all roles
  return {
    ...profile,
    workExperiences: profile.workExperiences.map((exp) => ({
      ...exp,
      description: formatBulletDescription(exp.description),
    })),
  };
}
